import { Project, ProjectCarton, ProjectPanel, ProjectHardware, User, ActivityLog, AuthData, FMSDBSchema, SyncStatus, CacheMetadataValue } from '../types';
import { openDB, IDBPDatabase } from 'idb';

interface CacheMetadata {
  key: string;
  value: CacheMetadataValue;
  timestamp: number;
  [key: string]: unknown; // Allow additional properties
}

const SYNC_INTERVAL = 5 * 60 * 1000; // 5 minutes
const MAX_LOG_AGE = 7 * 24 * 60 * 60 * 1000; // 7 days

interface CacheItem<T> {
  data: T;
  timestamp: number;
  userId: string;
  syncStatus: 'synced' | 'pending' | 'failed';
  lastSyncedAt?: number;
}

interface CacheConfig {
  authExpiryHours: number;
  dataExpiryHours: number;
  maxCacheSize: number; // in MB
  maxRecordsPerStore: number;
  syncInterval: number;
}

// Define the store names based on the schema
type StoreNames = 
  | 'projects' 
  | 'cartons' 
  | 'panels' 
  | 'hardware' 
  | 'activity_logs' 
  | 'metadata' 
  | 'auth';

export class CacheService {
  private static instance: CacheService;
  private dbName = 'FMS_Cache';
  private dbVersion = 1;
  private db: IDBPDatabase<FMSDBSchema> | null = null;
  private config: CacheConfig;
  private syncTimer: NodeJS.Timeout | null = null;

  private constructor() {
    this.config = {
      authExpiryHours: 8,
      dataExpiryHours: 24,
      maxCacheSize: 500,
      maxRecordsPerStore: 500000,
      syncInterval: SYNC_INTERVAL
    };
    this.initDB();
    this.startSyncTimer();
  }

  static getInstance(): CacheService {
    if (!CacheService.instance) {
      CacheService.instance = new CacheService();
    }
    return CacheService.instance;
  }

  private async initDB(): Promise<void> {
    this.db = await openDB<FMSDBSchema>(this.dbName, this.dbVersion, {
      upgrade: (db, oldVersion, newVersion) => this.handleUpgrade(db, oldVersion, newVersion || this.dbVersion),
    });
  }

  private async handleUpgrade(db: IDBPDatabase<FMSDBSchema>, _oldVersion: number, _newVersion: number): Promise<void> {
    // Create stores if they don't exist
    if (!db.objectStoreNames.contains('projects')) {
      const projectStore = db.createObjectStore('projects', { keyPath: 'id' });
      projectStore.createIndex('by-user', 'userId');
      projectStore.createIndex('by-status', 'status');
      projectStore.createIndex('by-date', 'created_at');
      projectStore.createIndex('by-sync-status', 'syncStatus');
      projectStore.createIndex('by-offline', 'isOfflineAvailable');
      projectStore.createIndex('by-timestamp', 'timestamp');
    }

    if (!db.objectStoreNames.contains('cartons')) {
      const cartonStore = db.createObjectStore('cartons', { keyPath: 'id' });
      cartonStore.createIndex('by-project', 'project_id');
      cartonStore.createIndex('by-user', 'userId');
      cartonStore.createIndex('by-status', 'is_received');
      cartonStore.createIndex('by-sync-status', 'syncStatus');
      cartonStore.createIndex('by-timestamp', 'timestamp');
    }

    if (!db.objectStoreNames.contains('panels')) {
      const panelStore = db.createObjectStore('panels', { keyPath: 'id' });
      panelStore.createIndex('by-carton', 'carton_id');
      panelStore.createIndex('by-user', 'userId');
      panelStore.createIndex('by-sync-status', 'syncStatus');
      panelStore.createIndex('by-timestamp', 'timestamp');
    }

    if (!db.objectStoreNames.contains('hardware')) {
      const hardwareStore = db.createObjectStore('hardware', { keyPath: 'id' });
      hardwareStore.createIndex('by-carton', 'carton_id');
      hardwareStore.createIndex('by-user', 'userId');
      hardwareStore.createIndex('by-sync-status', 'syncStatus');
      hardwareStore.createIndex('by-timestamp', 'timestamp');
    }

    if (!db.objectStoreNames.contains('auth')) {
      db.createObjectStore('auth', { keyPath: 'key' });
    }

    if (!db.objectStoreNames.contains('activity_logs')) {
      const logStore = db.createObjectStore('activity_logs', { keyPath: 'id' });
      logStore.createIndex('by-timestamp', 'timestamp');
      logStore.createIndex('by-type', 'type');
      logStore.createIndex('by-sync-status', 'synced');
      logStore.createIndex('by-user', 'userId');
    }

    if (!db.objectStoreNames.contains('metadata')) {
      db.createObjectStore('metadata', { keyPath: 'key' });
    }
  }

  public async ensureDB(): Promise<IDBPDatabase<FMSDBSchema>> {
    if (!this.db) {
      await this.initDB();
    }
    return this.db!;
  }

  async updateMetadata(storeName: string, totalRecords: number): Promise<void> {
    const db = await this.ensureDB();
    const tx = db.transaction('metadata', 'readwrite');
    const store = tx.objectStore('metadata');

    await store.put({
      key: `${storeName}_metadata`,
      value: {
        totalRecords,
        lastUpdated: Date.now(),
        version: 1,
        timestamp: Date.now()
      }
    });

    await tx.done;
  }

  public async getMetadata(key: string): Promise<string | null> {
    const db = await this.ensureDB();
    const tx = db.transaction('metadata', 'readonly');
    const store = tx.objectStore('metadata');

    const metadata = await store.get(`${key}_metadata`);
    if (!metadata) return null;
    
    // Ensure we return a string or null
    const value = (metadata as CacheMetadata).value;
    if (value === null || value === undefined) return null;
    return String(value);
  }

  // Projects Cache with Pagination
  async cacheProject(project: Project): Promise<void> {
    try {
      const db = await this.db;
      if (!db) {
        throw new Error('Database not initialized');
      }

      const tx = db.transaction('projects', 'readwrite');
      const store = tx.objectStore('projects');

      // Ensure the project has all required fields
      const projectToCache: Project = {
        ...project,
        id: project.id,
        userId: project.userId || '',
        syncStatus: project.syncStatus || 'synced',
        code: project.code || '',
        name: project.name || '',
        so_number: project.so_number || '',
        customer_name: project.customer_name || '',
        customer_contact: project.customer_contact || '',
        order_id: project.order_id || '',
        order_date: project.order_date || '',
        start_date: project.start_date || '',
        end_date: project.end_date || '',
        status: project.status || '',
        description: project.description || '',
        project_type: project.project_type || '',
        project_city: project.project_city || '',
        partner_name: project.partner_name || '',
        delivery_address: project.delivery_address || '',
        created_at: project.created_at || new Date().toISOString(),
        updated_at: project.updated_at || new Date().toISOString(),
        statistics: project.statistics || {
          cartons: { total_cartons: 0 },
          panels: { total_panels: 0 },
          hardware: { total_hardware: 0 }
        },
        timestamp: Date.now()
      };

      await store.put(projectToCache);
      await tx.done;
    } catch (error) {
      console.error('Error caching project:', error);
      throw error;
    }
  }

  async getCachedProject(projectId: string, userId: string): Promise<Project | null> {
    try {
      const db = await this.ensureDB();
      const transaction = db.transaction(['projects'], 'readonly');
      const store = transaction.objectStore('projects');

      const project = await store.get(projectId);
      return project?.userId === userId ? project : null;
    } catch (error) {
      console.error('Failed to get cached project:', error);
      return null;
    }
  }

  async getOfflineProjects(userId: string): Promise<Project[]> {
    try {
      const db = await this.ensureDB();
      const transaction = db.transaction(['projects'], 'readonly');
      const store = transaction.objectStore('projects');
      const index = store.index('by-offline');

      const projects = await index.getAll('true');
      return projects.filter(project => project.userId === userId);
    } catch (error) {
      console.error('Failed to get offline projects:', error);
      return [];
    }
  }

  async getLogsByUser(userId: string, limit = 100): Promise<ActivityLog[]> {
    try {
      const db = await this.ensureDB();
      const tx = db.transaction('activity_logs', 'readonly');
      const store = tx.objectStore('activity_logs');
      
      // Use getAll with a filter function to find logs by user
      const allLogs = await store.getAll();
      const userLogs = allLogs
        .filter(log => log.userId === userId)
        .sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0))
        .slice(0, limit);
        
      await tx.done;
      return userLogs;
    } catch (error) {
      console.error('Error getting logs by user:', error);
      throw error;
    }
  }

  // Authentication Cache
  async cacheAuth(token: string, user: User): Promise<void> {
    try {
      const db = await this.ensureDB();
      const transaction = db.transaction(['auth'], 'readwrite');
      const store = transaction.objectStore('auth');

      const authData: AuthData = {
        key: 'current_user',
        value: {
          token,
          user,
          expiresAt: Date.now() + (this.config.authExpiryHours * 60 * 60 * 1000)
        },
        timestamp: Date.now()
      };

      await store.put(authData);
    } catch (error) {
      console.error('Failed to cache auth:', error);
      throw error;
    }
  }

  async getCachedAuth(): Promise<{ token: string; user: User } | null> {
    try {
      const db = await this.ensureDB();
      const transaction = db.transaction(['auth'], 'readonly');
      const store = transaction.objectStore('auth');

      const authData = await store.get('current_user');
      if (!authData) return null;

      if (this.isExpired(authData.value.expiresAt, this.config.authExpiryHours)) {
        await this.clearAuth();
        return null;
      }

      return {
        token: authData.value.token,
        user: authData.value.user
      };
    } catch (error) {
      console.error('Failed to get cached auth:', error);
      return null;
    }
  }

  async clearAuth(): Promise<void> {
    try {
      const db = await this.ensureDB();
      const transaction = db.transaction(['auth'], 'readwrite');
      const store = transaction.objectStore('auth');
      await store.delete('current_user');
    } catch (error) {
      console.error('Failed to clear auth:', error);
      throw error;
    }
  }

  // Project Cartons Cache
  async cacheCarton(carton: ProjectCarton): Promise<void> {
    try {
      const db = await this.db;
      if (!db) {
        throw new Error('Database not initialized');
      }

      const tx = db.transaction('cartons', 'readwrite');
      const store = tx.objectStore('cartons');

      // Ensure the carton has all required fields
      const cartonToCache: ProjectCarton = {
        ...carton,
        id: carton.id,
        userId: carton.userId || '',
        syncStatus: carton.syncStatus || 'synced',
        project_id: carton.project_id,
        code: carton.code || '',
        status: carton.status || 'pending',
        is_received: carton.is_received || false,
        received_at: carton.received_at,
        received_by: carton.received_by,
        remarks: carton.remarks,
        timestamp: Date.now()
      };

      await store.put(cartonToCache);
      await tx.done;
    } catch (error) {
      console.error('Error caching carton:', error);
      throw error;
    }
  }

  async getCachedCartons(projectId: string): Promise<ProjectCarton[]> {
    const db = await this.ensureDB();
    const tx = db.transaction('cartons', 'readonly');
    const store = tx.objectStore('cartons');
    const index = store.index('by-project');
    const cartons = await index.getAll(projectId);
    return cartons;
  }

  async markCartonReceived(cartonId: string, userId: string): Promise<void> {
    try {
      const db = await this.ensureDB();
      const transaction = db.transaction(['cartons'], 'readwrite');
      const store = transaction.objectStore('cartons');

      const carton = await store.get(cartonId);
      if (carton && carton.userId === userId) {
        const updatedCarton: ProjectCarton = {
          ...carton,
          is_received: true,
          received_at: new Date().toISOString(),
          syncStatus: 'pending' as SyncStatus,
          timestamp: Date.now()
        };
        await store.put(updatedCarton);
      }
    } catch (error) {
      console.error('Failed to mark carton as received:', error);
      throw error;
    }
  }

  // Panel Methods
  async cachePanel(panel: ProjectPanel): Promise<void> {
    const db = await this.ensureDB();
    const tx = db.transaction('panels', 'readwrite');
    const store = tx.objectStore('panels');
    await store.put(panel);
    await tx.done;
  }

  async getCachedPanels(cartonId: string): Promise<ProjectPanel[]> {
    const db = await this.ensureDB();
    const tx = db.transaction('panels', 'readonly');
    const store = tx.objectStore('panels');
    const index = store.index('by-carton');
    const panels = await index.getAll(cartonId);
    return panels;
  }

  async markPanelReceived(panelId: string, userId: string): Promise<void> {
    try {
      const db = await this.ensureDB();
      const transaction = db.transaction(['panels'], 'readwrite');
      const store = transaction.objectStore('panels');

      const panel = await store.get(panelId);
      if (panel && panel.userId === userId) {
        const updatedPanel: ProjectPanel = {
          ...panel,
          is_received: true,
          received_at: new Date().toISOString(),
          syncStatus: 'pending' as SyncStatus,
          timestamp: Date.now()
        };
        await store.put(updatedPanel);
      }
    } catch (error) {
      console.error('Failed to mark panel as received:', error);
      throw error;
    }
  }

  // Hardware Methods
  async cacheHardware(hardware: ProjectHardware): Promise<void> {
    const db = await this.ensureDB();
    const tx = db.transaction('hardware', 'readwrite');
    const store = tx.objectStore('hardware');
    await store.put(hardware);
    await tx.done;
  }

  async getCachedHardware(cartonId: string): Promise<ProjectHardware[]> {
    const db = await this.ensureDB();
    const tx = db.transaction('hardware', 'readonly');
    const store = tx.objectStore('hardware');
    const index = store.index('by-carton');
    const hardware = await index.getAll(cartonId);
    return hardware;
  }

  async markHardwareReceived(hardwareId: string, userId: string): Promise<void> {
    try {
      const db = await this.ensureDB();
      const transaction = db.transaction(['hardware'], 'readwrite');
      const store = transaction.objectStore('hardware');

      const hardware = await store.get(hardwareId);
      if (hardware && hardware.userId === userId) {
        const updatedHardware: ProjectHardware = {
          ...hardware,
          is_received: true,
          received_at: new Date().toISOString(),
          syncStatus: 'pending' as SyncStatus,
          timestamp: Date.now()
        };
        await store.put(updatedHardware);
      }
    } catch (error) {
      console.error('Failed to mark hardware as received:', error);
      throw error;
    }
  }

  // Cache Management
  async getCacheSize(): Promise<number> {
    try {
      const db = await this.ensureDB();
      const stores = db.objectStoreNames;
      let totalSize = 0;

      for (const storeName of stores) {
        const transaction = db.transaction(storeName, 'readonly');
        const store = transaction.objectStore(storeName);
        const count = await store.count();
        totalSize += count;
      }

      return totalSize;
    } catch (error) {
      console.error('Failed to get cache size:', error);
      return 0;
    }
  }

  private isExpired(timestamp?: number, expiryHours: number = this.config.dataExpiryHours): boolean {
    if (!timestamp) return true;
    const expiryMs = expiryHours * 60 * 60 * 1000;
    return Date.now() - timestamp > expiryMs;
  }

  async clearExpiredCache(): Promise<void> {
    try {
      const stores: StoreNames[] = [
        'projects', 
        'cartons', 
        'panels', 
        'hardware', 
        'activity_logs',
        'metadata',
        'auth'
      ];

      const db = await this.ensureDB();
      
      for (const storeName of stores) {
        const tx = db.transaction(storeName, 'readwrite');
        const store = tx.objectStore(storeName);
        
        try {
          // Get all records and filter by timestamp
          const allItems = await store.getAll();
          
          // Delete expired items
          const deletePromises = allItems
            .filter(item => {
              // Only delete items that have a timestamp and are expired
              // Skip auth and metadata stores as they have different expiration rules
              if (storeName === 'auth' || storeName === 'metadata') {
                return false;
              }
              
              const itemWithTimestamp = item as { timestamp?: number; id?: string | number };
              return itemWithTimestamp.timestamp && this.isExpired(itemWithTimestamp.timestamp, this.config.dataExpiryHours);
            })
            .map(item => store.delete((item as { id: string | number }).id!));
          
          if (deletePromises.length > 0) {
            await Promise.all(deletePromises);
          }
          await tx.done;
        } catch (error) {
          console.error(`Error processing store ${storeName}:`, error);
          // Continue with next store even if one fails
          continue;
        }
      }
    } catch (error) {
      console.error('Error in clearExpiredCache:', error);
      throw error;
    }
  }

  async clearAllCache(): Promise<void> {
    try {
      const db = await this.ensureDB();
      const storeNames: StoreNames[] = [
        'projects', 'cartons', 'panels', 'hardware', 
        'activity_logs', 'metadata', 'auth'
      ];

      for (const storeName of storeNames) {
        try {
          const tx = db.transaction(storeName, 'readwrite');
          const store = tx.objectStore(storeName);
          await store.clear();
          await tx.done;
        } catch (error) {
          console.error(`Error clearing store ${storeName}:`, error);
          // Continue with next store even if one fails
          continue;
        }
      }
    } catch (error) {
      console.error('Failed to clear cache:', error);
      throw error;
    }
  }

  private isExpired(timestamp?: number, expiryHours: number = this.config.dataExpiryHours): boolean {
    if (!timestamp) return false;
    const expiryTime = timestamp + (expiryHours * 60 * 60 * 1000);
    return Date.now() > expiryTime;
  }

  // Logging Methods
  async logActivity(type: string, action: string, details: string, userId: string): Promise<void> {
    const db = await this.ensureDB();
    const tx = db.transaction('activity_logs', 'readwrite');
    const store = tx.objectStore('activity_logs');

    const activityLog: ActivityLog = {
      id: Date.now().toString(),
      type,
      action,
      details,
      synced: false,
      userId,
      timestamp: Date.now(),
      syncStatus: 'pending'
    };

    await store.add(activityLog);
    await tx.done;
  }

  // Sync Methods
  private startSyncTimer(): void {
    if (this.syncTimer) {
      clearInterval(this.syncTimer);
    }
    this.syncTimer = setInterval(async () => {
      try {
        await this.syncPendingChanges();
      } catch (error) {
        console.error('Error in sync timer:', error);
      }
    }, this.config.syncInterval);
  }

  private async syncPendingChanges(): Promise<void> {
    try {
      const db = await this.ensureDB();
      const pendingItems = await this.getPendingItems(db);
      
      for (const item of pendingItems) {
        try {
          await this.syncItem(item);
        } catch (error) {
          console.error(`Failed to sync item:`, error);
          await this.markItemAsFailed(item);
        }
      }
    } catch (error) {
      console.error('Failed to sync pending changes:', error);
    }
  }

  private async getPendingItems(db: IDBPDatabase<FMSDBSchema>): Promise<any[]> {
    const pendingItems = [];
    
    // Get pending cartons
    const cartonTx = db.transaction(['cartons'], 'readonly');
    const cartonStore = cartonTx.objectStore('cartons');
    const cartonIndex = cartonStore.index('by-sync-status');
    const pendingCartons = await cartonIndex.getAll('pending');
    pendingItems.push(...pendingCartons);

    // Get pending panels
    const panelTx = db.transaction(['panels'], 'readonly');
    const panelStore = panelTx.objectStore('panels');
    const panelIndex = panelStore.index('by-sync-status');
    const pendingPanels = await panelIndex.getAll('pending');
    pendingItems.push(...pendingPanels);

    // Get pending hardware
    const hardwareTx = db.transaction(['hardware'], 'readonly');
    const hardwareStore = hardwareTx.objectStore('hardware');
    const hardwareIndex = hardwareStore.index('by-sync-status');
    const pendingHardware = await hardwareIndex.getAll('pending');
    pendingItems.push(...pendingHardware);

    return pendingItems;
  }

  private async syncItem(item: any): Promise<void> {
    // Implement API sync logic here
    // This should match the Laravel API endpoints
    const endpoint = this.getSyncEndpoint(item);
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify(item)
    });

    if (!response.ok) {
      throw new Error(`Failed to sync item: ${response.statusText}`);
    }

    await this.markItemAsSynced(item);
  }

  private getSyncEndpoint(item: any): string {
    // Map item type to appropriate API endpoint
    switch (item.type) {
      case 'carton':
        return `/api/cartons/${item.id}/sync`;
      case 'panel':
        return `/api/panels/${item.id}/sync`;
      case 'hardware':
        return `/api/hardware/${item.id}/sync`;
      default:
        throw new Error(`Unknown item type: ${item.type}`);
    }
  }

  private async markItemAsSynced(item: any): Promise<void> {
    const db = await this.ensureDB();
    const store = this.getStoreForItem(db, item);
    
    const updatedItem = {
      ...item,
      syncStatus: 'synced',
      lastSyncedAt: Date.now(),
      timestamp: Date.now()
    };
    await store.put(updatedItem);
  }

  private async markItemAsFailed(item: any): Promise<void> {
    const db = await this.ensureDB();
    const store = this.getStoreForItem(db, item);
    
    const updatedItem = {
      ...item,
      syncStatus: 'failed',
      timestamp: Date.now()
    };
    await store.put(updatedItem);
  }

  private getStoreForItem(db: IDBPDatabase<FMSDBSchema>, item: any) {
    switch (item.type) {
      case 'carton':
        return db.transaction(['cartons'], 'readwrite').objectStore('cartons');
      case 'panel':
        return db.transaction(['panels'], 'readwrite').objectStore('panels');
      case 'hardware':
        return db.transaction(['hardware'], 'readwrite').objectStore('hardware');
      default:
        throw new Error(`Unknown item type: ${item.type}`);
    }
  }

  // Set method for activity logs
  async set(_storeName: 'activity_logs', value: ActivityLog): Promise<void> {
    try {
      const db = await this.ensureDB();
      const tx = db.transaction('activity_logs', 'readwrite');
      const store = tx.objectStore('activity_logs');
      
      const timestamp = typeof value.timestamp === 'string' 
        ? new Date(value.timestamp).getTime() 
        : value.timestamp || Date.now();
      
      const log: ActivityLog = {
        ...value,
        timestamp,
        synced: value.synced || false,
        userId: value.userId || localStorage.getItem('userId') || 'system',
        details: typeof value.details === 'string' ? value.details : JSON.stringify(value.details || {})
      };
      
      await store.put(log);
      await tx.done;
    } catch (error) {
      console.error('Error setting activity log:', error);
      throw error;
    }
  }

  // Keep these methods for future use
  async getUnsyncedLogs(limit = 100): Promise<ActivityLog[]> {
    try {
      const db = await this.ensureDB();
      const tx = db.transaction('activity_logs', 'readonly');
      const store = tx.objectStore('activity_logs');
      
      // Use getAll with a filter function to find unsynced logs
      const allLogs = await store.getAll();
      const unsyncedLogs = allLogs
        .filter(log => log.synced === false)
        .slice(0, limit);
        
      await tx.done;
      return unsyncedLogs;
    } catch (error) {
      console.error('Error getting unsynced logs:', error);
      throw error;
    }
  }

  async clearOldLogs(): Promise<void> {
    try {
      const db = await this.ensureDB();
      const transaction = db.transaction(['activity_logs'], 'readwrite');
      const store = transaction.objectStore('activity_logs');
      const index = store.index('by-timestamp');

      const cutoffTime = Date.now() - MAX_LOG_AGE;
      const keysToDelete = [];
      
      // First, find all keys that need to be deleted
      let cursor = await index.openCursor();
      while (cursor) {
        if (cursor.value.timestamp < cutoffTime) {
          keysToDelete.push(cursor.primaryKey);
        }
        cursor = await cursor.continue();
      }
      
      // Then delete them in a separate transaction
      if (keysToDelete.length > 0) {
        const deleteTx = db.transaction(['activity_logs'], 'readwrite');
        const deleteStore = deleteTx.objectStore('activity_logs');
        
        for (const key of keysToDelete) {
          await deleteStore.delete(key);
        }
        
        await deleteTx.done;
      }
    } catch (error) {
      console.error('Failed to clear old logs:', error);
    }
  }

  // Cleanup
  async cleanup(): Promise<void> {
    if (this.syncTimer) {
      clearInterval(this.syncTimer);
    }
    await this.clearOldLogs();
  }

  // Metadata Methods
  public async setMetadata(key: string, value: string): Promise<void> {
    const db = await this.ensureDB();
    const tx = db.transaction('metadata', 'readwrite');
    const store = tx.objectStore('metadata');
    
    // Store the value as a string in the metadata
    const metadata: CacheMetadata = {
      key: `${key}_metadata`,
      value: value as unknown as CacheMetadataValue,
      timestamp: Date.now()
    };
    await store.put(metadata);
    
    await tx.done;
  }

  async getCacheMetadata(storeName: StoreNames): Promise<CacheMetadataValue | null> {
    const db = await this.ensureDB();
    const tx = db.transaction('metadata', 'readonly');
    const store = tx.objectStore('metadata');
    
    const metadata = await store.get(storeName);
    return metadata?.value || null;
  }

  /**
   * Cache multiple projects
   */
  async cacheProjects(projects: Project[]): Promise<void> {
    const db = await this.ensureDB();
    const tx = db.transaction('projects', 'readwrite');
    const store = tx.objectStore('projects');

    for (const project of projects) {
      await store.put(project);
    }

    await tx.done;
  }

  /**
   * Get all cached projects for a user
   */
  async getCachedProjects(): Promise<Project[]> {
    const db = await this.ensureDB();
    const tx = db.transaction('projects', 'readonly');
    const store = tx.objectStore('projects');
    const projects = await store.getAll();
    return projects;
  }
}

// Export a singleton instance
export const cacheService = CacheService.getInstance();
export default cacheService;

// Add at the bottom, before export
const SELECTED_PROJECT_KEY = 'selected_project_id';

export async function setSelectedProjectId(id: string | null) {
  const db = await cacheService.ensureDB();
  const tx = db.transaction('metadata', 'readwrite');
  const store = tx.objectStore('metadata');
  if (id) {
    await store.put({ key: SELECTED_PROJECT_KEY, value: id });
  } else {
    await store.delete(SELECTED_PROJECT_KEY);
  }
  await tx.done;
}

export async function getSelectedProjectId(): Promise<string | null> {
  const db = await cacheService.ensureDB();
  const tx = db.transaction('metadata', 'readonly');
  const store = tx.objectStore('metadata');
  const result = await store.get(SELECTED_PROJECT_KEY);
  return result ? result.value : null;
} 